import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styles from './OfferManagementDetail.module.scss'
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import { useNavigate, useParams } from 'react-router-dom'
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import OfferModal from './components/OfferModal'
import { useRef } from 'react'
import RefuseModal from './components/RefuseModal'
import AcceptModal from './components/AcceptModal'
import SpoeumModal from '../../../components/modal/SpoeumModal'
import { useAuthStatus, formatAmount, getYYYYMMString, useToken } from '../../../utils'
import { ArrowsRightLeftIcon } from '@heroicons/react/24/solid'
import HistoryModal from './HistoryModal'
import {
	useOfferDetail,
	useRejectOffer,
	useAcceptOffer,
	useMarkOfferAsVisited,
} from '../../../hooks'
import { formatPrizeTypeLabel, INCENTIVE_BASIS_LABEL } from '../../../utils/incentiveDisplay'
// ProDisplay / CaddieDisplay 재활용은 보류

// 계약 타입별 필드 설정 정의
const CONTRACT_FIELD_CONFIGS = {
	termContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			changedPath: 'termContract.isSalaryTaxIncluded',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			changedPath: 'termContract.payrollCycles',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'salaryAmountForPeriod',
			label: '기간 급여액',
			changedPath: 'termContract.salaryAmountForPeriod',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			changedPath: 'termContract.prizeType',
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			changedPath: 'termContract.incentives',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursement',
			label: '해외대회 지원금',
			changedPath: 'termContract.expenseReimbursement',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)}%`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용',
			changedPath: 'termContract.wearSponsoredHat',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
	domesticContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			changedPath: 'domesticContract.isSalaryTaxIncluded',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			changedPath: 'domesticContract.payrollCycles',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'weeklySalaryCutThrough',
			label: '컷통과',
			changedPath: 'domesticContract.weeklySalaryCutThrough',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'weeklySalaryCutFail',
			label: '컷탈락',
			changedPath: 'domesticContract.weeklySalaryCutFail',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			changedPath: 'domesticContract.prizeType',
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			changedPath: 'domesticContract.incentives',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursementJeju',
			label: '제주 대회 지원금',
			changedPath: 'domesticContract.expenseReimbursementJeju',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)} 원`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용',
			changedPath: 'domesticContract.wearSponsoredHat',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
	overseasContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			changedPath: 'overseasContract.isSalaryTaxIncluded',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			changedPath: 'overseasContract.payrollCycles',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'weeklySalaryCutThrough',
			label: '컷통과',
			changedPath: 'overseasContract.weeklySalaryCutThrough',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'weeklySalaryCutFail',
			label: '컷탈락',
			changedPath: 'overseasContract.weeklySalaryCutFail',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			changedPath: 'overseasContract.prizeType',
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			changedPath: 'overseasContract.incentives',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursement',
			label: '해외 대회 지원금',
			changedPath: 'overseasContract.expenseReimbursement',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)} %`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용',
			changedPath: 'overseasContract.wearSponsoredHat',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
}

export const OfferManagementDetail = React.memo(() => {
	const { offerId } = useParams()

	// React Query hook 사용
	const { data: suggestManagementDetail, isLoading, error, refetch } = useOfferDetail(offerId)
	const rejectOfferMutation = useRejectOffer()
	const acceptOfferMutation = useAcceptOffer()
	const markOfferAsVisitedMutation = useMarkOfferAsVisited()

	// 히스토리 데이터 처리
	const offerHistory = suggestManagementDetail?.Offer_history || []
	const sortedOfferHistory =
		offerHistory.length > 1
			? [...offerHistory].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
			: offerHistory
	const isFirstOffer = offerHistory.length === 1
	const latestRenegotiation = sortedOfferHistory[0]

	const [isFavorite, setIsFavorite] = useState(false)
	const [offerModalOpen, setOfferModalOpen] = useState(false)
	const navigate = useNavigate()
	const [tabStatus, setTabStatus] = useState('offerContract')
	const [domesticOverseasSwapContent, setDomesticOverseasSwapContent] = useState(true)
	const [refuseModalOpen, setRefuseModalOpen] = useState(false)
	const [acceptModalOpen, setAcceptModalOpen] = useState(false)
	const [contractConfirmModalOpen, setContractConfirmModalOpen] = useState(false)
	const [authStatus] = useAuthStatus()
	const [whichToLook, setWhichToLook] = useState(null)
	const [haveToIndicateChanges, setHaveToIndicateChanges] = useState(false)
	const offerModalRef = useRef(null)
	const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
	const [isHistoryModalDragging, setIsHistoryModalDragging] = useState(false)
	const [historyModalDragOffset, setHistoryModalDragOffset] = useState({ x: 0, y: 0 })
	const [historyModalPosition, setHistoryModalPosition] = useState({ x: 0, y: 0 })
	const [historyModalHasBeenDragged, setHistoryModalHasBeenDragged] = useState(false)
	const modalRef = useRef(null)
	const historyModalRef = useRef(null)
	const [isDragging, setIsDragging] = useState(false)
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
	const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 })
	const [hasBeenDragged, setHasBeenDragged] = useState(false)

	const token = useToken()

	useEffect(() => {
		if (suggestManagementDetail) {
			console.log('suggestManagementDetail : ', suggestManagementDetail)
		}
	}, [suggestManagementDetail])

	// ESC 키로 모달 닫기
	const handleKeyDown = useCallback(
		(e) => {
			if (e.key === 'Escape') {
				if (isHistoryModalOpen) {
					setIsHistoryModalOpen(false)
				}
				if (offerModalOpen) {
					setOfferModalOpen(false)
				}
			}
		},
		[isHistoryModalOpen, offerModalOpen],
	)

	// 투어 데이터에서 해외 투어 포함 여부 계산을 useMemo로 최적화
	const overseasTourIncluded = useMemo(() => {
		if (suggestManagementDetail?.termContract) {
			const { isOverseasIncluded, expenseReimbursement } =
				suggestManagementDetail.termContract
			if (isOverseasIncluded) return true
			if (
				typeof expenseReimbursement === 'number' &&
				!Number.isNaN(expenseReimbursement) &&
				expenseReimbursement > 0
			) {
				return true
			}
		}
		if (!suggestManagementDetail?.Offer_tour?.length) return false
		return suggestManagementDetail.Offer_tour.some(
			(tour) => tour.tour.region === 'INTERNATIONAL' || tour.tour.region === 'BOTH',
		)
	}, [
		suggestManagementDetail?.Offer_tour,
		suggestManagementDetail?.termContract,
		suggestManagementDetail?.termContract?.expenseReimbursement,
		suggestManagementDetail?.termContract?.isOverseasIncluded,
	])

	// 투어 카테고리 계산을 useMemo로 최적화 - user_type이 tourPro인 사람의 tour_type 사용
	const tourCategories = useMemo(() => {
		if (!suggestManagementDetail) return null

		// user와 targetUser 중에서 user_type이 tourPro인 사람 찾기
		const proUser =
			suggestManagementDetail.user?.user_type === 'tourPro'
				? suggestManagementDetail.user
				: suggestManagementDetail.targetUser?.user_type === 'tourPro'
					? suggestManagementDetail.targetUser
					: null

		return proUser?.user_tour_pro?.tour_type || null
	}, [suggestManagementDetail])

	// 계약 기간 계산을 useMemo로 최적화
	const contractPeriods = useMemo(() => {
		if (!suggestManagementDetail?.Offer_tour?.length) return { startDate: null, endDate: null }

		const earliestStartDate = suggestManagementDetail.Offer_tour.reduce((earliest, tour) => {
			return tour.tour.started_at < earliest ? tour.tour.started_at : earliest
		}, suggestManagementDetail.Offer_tour[0].tour.started_at)

		const latestEndDate = suggestManagementDetail.Offer_tour.reduce((latest, tour) => {
			return tour.tour.ended_at > latest ? tour.tour.ended_at : latest
		}, suggestManagementDetail.Offer_tour[0].tour.ended_at)

		return { startDate: earliestStartDate, endDate: latestEndDate }
	}, [suggestManagementDetail?.Offer_tour])

	const rawContractType = suggestManagementDetail?.offer_type
		? suggestManagementDetail.offer_type.toLowerCase()
		: ''

	const normalizedContractType = useMemo(() => {
		if (['short', 'long', 'period'].includes(rawContractType)) return rawContractType
		if (suggestManagementDetail?.termContract) return 'period'
		const offerTourLength = suggestManagementDetail?.Offer_tour?.length || 0
		if (offerTourLength === 1) return 'short'
		if (offerTourLength > 1) return 'long'
		return 'period'
	}, [
		rawContractType,
		suggestManagementDetail?.termContract,
		suggestManagementDetail?.Offer_tour?.length,
	])

	const termTypeValue = suggestManagementDetail?.term_type || ''

	const contractTypeLabel = useMemo(() => {
		if (normalizedContractType === 'short') return '단기계약'
		if (normalizedContractType === 'long') return '장기계약'
		return '기간계약'
	}, [normalizedContractType])

	const periodContractText = useMemo(() => {
		if (normalizedContractType === 'period') {
			const lowerTerm = termTypeValue.toLowerCase()
			if (['full_year', 'full-year', 'both'].includes(lowerTerm)) return '상반기 / 하반기'
			if (['year_first_half', 'first_half', 'first-half'].includes(lowerTerm)) return '상반기'
			if (['year_second_half', 'second_half', 'second-half'].includes(lowerTerm))
				return '하반기'
			return '기간 미정'
		}

		const { startDate, endDate } = contractPeriods
		if (startDate && endDate) {
			return `${getYYYYMMString(startDate).slice(0, 8)} ~ ${getYYYYMMString(endDate).slice(0, 8)}`
		}
		if (startDate || endDate) {
			const singleDate = startDate || endDate
			return getYYYYMMString(singleDate).slice(0, 8)
		}
		return '기간 미정'
	}, [normalizedContractType, termTypeValue, contractPeriods])

	// 경력 레벨 매핑 (OfferItem과 동일)
	const mapCareerLevelToText = {
		NEWBIE: '신입',
		ONE_TO_THREE: '1~3년차',
		FOUR_TO_SIX: '4~6년차',
		SEVEN_TO_TEN: '7~10년차',
		OVER_TEN: '10년 이상',
	}

	// 등수 매핑 함수
	const mapRankToNumber = (rank) => {
		const rankMap = {
			FIRST: '1',
			SECOND: '2',
			THIRD: '3',
			FOURTH: '4',
			FIFTH: '5',
			SIXTH: '6',
			SEVENTH: '7',
			EIGHTH: '8',
			NINTH: '9',
			TENTH: '10',
		}
		return rankMap[rank] || rank
	}

	// 프로필 표시 정보 계산 (OfferItem과 동일한 로직)
	const displayTitle = useMemo(() => {
		if (!whichToLook) return ''

		const type = whichToLook?.user_type === 'tourPro' ? '프로' : '캐디'
		const name = whichToLook?.name
		const tour =
			whichToLook?.user_type === 'tourPro'
				? whichToLook.user_tour_pro?.tour_type
				: whichToLook.user_tour_caddie?.tour_type
		const career =
			whichToLook?.user_type === 'tourPro'
				? mapCareerLevelToText[whichToLook.user_tour_pro?.career_level]
				: mapCareerLevelToText[whichToLook.user_tour_caddie?.career_level]

		if (whichToLook?.user_type === 'tourPro') {
			return `${tour} / ${name} / ${career}`
		} else {
			return `${type} / ${name} / ${career}`
		}
	}, [whichToLook])

	// 소속팀과 스폰서 정보 계산
	const displaySubInfo = useMemo(() => {
		if (!whichToLook) return ''

		if (whichToLook?.user_type === 'tourPro') {
			// 프로: agency와 sponsor 표시
			let secondText = ''
			// agency는 객체 또는 문자열일 수 있음
			const agencyName =
				whichToLook?.user_tour_pro?.agency?.name || whichToLook?.user_tour_pro?.agency_name
			if (agencyName) {
				secondText = agencyName + ' / '
			} else {
				secondText = '무소속' + ' / '
			}
			// main_sponsor도 객체 또는 문자열일 수 있음
			const sponsorName =
				whichToLook?.user_tour_pro?.main_sponsor?.name ||
				whichToLook?.user_tour_pro?.main_sponsor
			if (sponsorName) {
				secondText += sponsorName
			} else {
				secondText += ' -'
			}
			return secondText
		} else if (whichToLook?.user_type === 'tourCaddie') {
			// 캐디: 팀만 표시
			const teamName = whichToLook?.user_tour_caddie?.caddie_team_name
			return teamName || '무소속'
		}

		return ''
	}, [whichToLook])

	// whichToLook 설정 및 변경사항 표시 로직
	useEffect(() => {
		let whichToLook
		if (authStatus?.user?.id === suggestManagementDetail?.user_id) {
			whichToLook = suggestManagementDetail?.targetUser
		} else {
			//받는 제안
			whichToLook = suggestManagementDetail?.user
			// 제안을 받은 사람만 변경사항을 확인해야 함 (보낸 사람은 확인할 필요 없음)
			if (!isFirstOffer && authStatus?.user?.id === suggestManagementDetail?.target_user_id) {
				setHaveToIndicateChanges(true)
				console.log('haveToIndicateChanges set to true')
				console.log('isFirstOffer:', isFirstOffer)
				console.log('authStatus?.user?.id:', authStatus?.user?.id)
				console.log(
					'suggestManagementDetail?.target_user_id:',
					suggestManagementDetail?.target_user_id,
				)
				console.log('latestRenegotiation:', latestRenegotiation)
			}
		}

		setWhichToLook(whichToLook)
	}, [
		suggestManagementDetail?.user_id,
		suggestManagementDetail?.target_user_id,
		suggestManagementDetail?.user,
		suggestManagementDetail?.targetUser,
		authStatus?.user?.id,
		isFirstOffer,
	])

	// 아이템 상태 계산을 useMemo로 최적화
	const currentItemStatus = useMemo(() => {
		if (!suggestManagementDetail?.offer_status) return null

		const statusMap = {
			PRE: '대기중',
			PROCEEDING: '협상중',
			REJECT: '거절한 제안',
			REJECTED: '거절된 제안',
			OUTDATED: '기간만료',
			CONTRACT_PROPOSED: '계약 제안됨',
		}

		return statusMap[suggestManagementDetail.offer_status] || null
	}, [suggestManagementDetail?.offer_status])

	// CONTRACT_PROPOSED 상태 여부 확인
	const isContractProposed = useMemo(() => {
		return suggestManagementDetail?.offer_status === 'CONTRACT_PROPOSED'
	}, [suggestManagementDetail?.offer_status])

	// 로딩 상태나 에러 처리
	useEffect(() => {
		console.log('currentItemStatus:', currentItemStatus)
	}, [currentItemStatus])

	// ESC 키 이벤트 리스너 추가
	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [handleKeyDown])

	// 컴포넌트 마운트 시 제안 방문 상태 업데이트
	useEffect(() => {
		if (offerId) {
			if (suggestManagementDetail?.is_visited === false) {
				markOfferAsVisitedMutation.mutate(offerId)
			}
		}
	}, [offerId, suggestManagementDetail])

	// 계산된 값들을 직접 사용 (불필요한 상태 동기화 제거)

	// 디버깅용 로그 제거 (성능 최적화)
	// useEffect(() => {
	// 	console.log('suggestManagementDetail:', suggestManagementDetail)
	// }, [suggestManagementDetail])

	// 마우스 이벤트 핸들러들을 useCallback으로 최적화
	const handleMouseDown = useCallback((e) => {
		if (offerModalRef.current) {
			const rect = offerModalRef.current.getBoundingClientRect()
			setDragOffset({
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			})
			setIsDragging(true)
		}
	}, [])

	const handleHistoryModalMouseDown = useCallback((e) => {
		if (historyModalRef.current) {
			const rect = historyModalRef.current.getBoundingClientRect()
			setHistoryModalDragOffset({
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			})
			setIsHistoryModalDragging(true)
		}
	}, [])

	const handleMouseMove = useCallback(
		(e) => {
			if (isDragging && offerModalRef.current) {
				const newX = e.clientX - dragOffset.x
				const newY = e.clientY - dragOffset.y

				// 화면 경계 체크
				const maxX = window.innerWidth - offerModalRef.current.offsetWidth
				const maxY = window.innerHeight - offerModalRef.current.offsetHeight

				const clampedX = Math.max(0, Math.min(newX, maxX))
				const clampedY = Math.max(0, Math.min(newY, maxY))

				offerModalRef.current.style.transform = `translate(${clampedX}px, ${clampedY}px)`

				// 위치 업데이트
				setModalPosition({ x: clampedX, y: clampedY })
				setHasBeenDragged(true)
			}
		},
		[isDragging, dragOffset],
	)

	const handleHistoryModalMouseMove = useCallback(
		(e) => {
			if (isHistoryModalDragging && historyModalRef.current) {
				const newX = e.clientX - historyModalDragOffset.x
				const newY = e.clientY - historyModalDragOffset.y

				// 화면 경계 체크
				const maxX = window.innerWidth - historyModalRef.current.offsetWidth
				const maxY = window.innerHeight - historyModalRef.current.offsetHeight

				const clampedX = Math.max(0, Math.min(newX, maxX))
				const clampedY = Math.max(0, Math.min(newY, maxY))

				historyModalRef.current.style.transform = `translate(${clampedX}px, ${clampedY}px)`

				// 위치 업데이트
				setHistoryModalPosition({ x: clampedX, y: clampedY })
				setHistoryModalHasBeenDragged(true)
			}
		},
		[isHistoryModalDragging, historyModalDragOffset],
	)

	const handleMouseUp = useCallback(() => {
		setIsDragging(false)
		setIsHistoryModalDragging(false)
	}, [])

	useEffect(() => {
		if (isDragging || isHistoryModalDragging) {
			document.addEventListener(
				'mousemove',
				isDragging ? handleMouseMove : handleHistoryModalMouseMove,
			)
			document.addEventListener('mouseup', handleMouseUp)
			return () => {
				document.removeEventListener(
					'mousemove',
					isDragging ? handleMouseMove : handleHistoryModalMouseMove,
				)
				document.removeEventListener('mouseup', handleMouseUp)
			}
		}
	}, [isDragging, isHistoryModalDragging, dragOffset, historyModalDragOffset])

	// 모달이 열릴 때 위치 설정
	useEffect(() => {
		if (offerModalOpen && offerModalRef.current) {
			let targetX, targetY

			if (hasBeenDragged) {
				// 이전에 드래그한 위치가 있으면 그 위치 사용
				targetX = modalPosition.x
				targetY = modalPosition.y
			} else {
				// 처음 열리는 경우 화면 중앙에 위치
				const modalWidth = 360
				const modalHeight = 640
				targetX = (window.innerWidth - modalWidth) / 2
				targetY = (window.innerHeight - modalHeight) / 2
			}

			offerModalRef.current.style.transform = `translate(${targetX}px, ${targetY}px)`
		}
	}, [offerModalOpen, hasBeenDragged, modalPosition])

	// 히스토리 모달이 열릴 때 위치 설정
	useEffect(() => {
		if (isHistoryModalOpen && historyModalRef.current) {
			let targetX, targetY

			if (historyModalHasBeenDragged) {
				// 이전에 드래그한 위치가 있으면 그 위치 사용
				targetX = historyModalPosition.x
				targetY = historyModalPosition.y
			} else {
				// 처음 열리는 경우 왼쪽 30%, 위쪽 50% 위치
				const modalWidth = 400
				const modalHeight = 500
				targetX = window.innerWidth * 0.3 - modalWidth / 2
				targetY = window.innerHeight * 0.5 - modalHeight / 2
			}

			historyModalRef.current.style.transform = `translate(${targetX}px, ${targetY}px)`
		}
	}, [isHistoryModalOpen, historyModalHasBeenDragged, historyModalPosition])

	// 페이지를 벗어날 때 모달 위치 초기화
	useEffect(() => {
		return () => {
			setHasBeenDragged(false)
			setModalPosition({ x: 0, y: 0 })
			setHistoryModalHasBeenDragged(false)
			setHistoryModalPosition({ x: 0, y: 0 })
		}
	}, [])

	const sortTourByDate = (tour) => {
		if (!tour || !Array.isArray(tour)) {
			return []
		}
		return tour.sort((a, b) => {
			const startDateA = new Date(a.tour.started_at)
			const startDateB = new Date(b.tour.started_at)

			// 시작 날짜가 다르면 시작 날짜로 정렬 (빠른 순)
			if (startDateA.getTime() !== startDateB.getTime()) {
				return startDateA - startDateB
			}

			// 시작 날짜가 같으면 끝나는 날짜로 정렬 (빨리 끝나는 순)
			const endDateA = new Date(a.tour.ended_at)
			const endDateB = new Date(b.tour.ended_at)
			return endDateA - endDateB
		})
	}

	const sortIncentiveSettings = useCallback((incentiveSettings) => {
		if (!incentiveSettings || !Array.isArray(incentiveSettings)) {
			return []
		}

		const sorted = incentiveSettings.sort((a, b) => {
			// startRank가 null이나 undefined인 경우 처리
			const aRank = a.startRank || 999
			const bRank = b.startRank || 999
			return aRank - bRank
		})

		return sorted
	}, [])

	// 필드 렌더링 헬퍼 함수들
	const renderContractField = useCallback(
		(config, contractData, contractType) => {
			const value = contractData?.[config.key]
			const isChanged =
				// haveToIndicateChanges &&
				latestRenegotiation?.changedFields?.includes(config.changedPath)

			return (
				<div
					key={config.key}
					className={`${styles.fieldContainer} ${isChanged ? styles.changedField : ''}`}>
					<div className={styles.fieldLabel}>{config.label}</div>
					<div className={styles.fieldContent}>
						{config.formatter ? config.formatter(value) : value}
					</div>
				</div>
			)
		},
		[latestRenegotiation?.changedFields],
	)

	const renderIncentiveField = useCallback(
		(config, incentives, contractType) => {
			const isChanged =
				// haveToIndicateChanges &&
				latestRenegotiation?.changedFields?.includes(config.changedPath)

			const sortedIncentives = sortIncentiveSettings(incentives)

			return (
				<div
					key={config.key}
					className={`${styles.fieldContainer} ${isChanged ? styles.changedField : ''}`}>
					<div className={styles.fieldLabel}>{config.label}</div>
					<div className={styles.fieldContent}>
						{sortedIncentives && sortedIncentives.length > 0 ? (
							<div className={styles.incentiveList}>
								{sortedIncentives.map((setting, mapIndex) => {
									if (setting.percentage === null && setting.endRank === null)
										return null

									const displayRange = (() => {
										if (mapIndex === 0 || setting.startRank === 1) return '우승'
										if (setting.startRank === null && setting.endRank === null)
											return '순위 미정'
										if (setting.endRank === 0)
											return `${setting.startRank}위 ~ CUT`
										if (
											setting.startRank !== null &&
											setting.endRank !== null &&
											setting.startRank !== setting.endRank
										) {
											return `${setting.startRank}위 ~ ${setting.endRank}위`
										}
										if (setting.startRank !== null && setting.endRank === null)
											return `${setting.startRank}위 ~`
										if (setting.startRank !== null)
											return `${setting.startRank}위`
										return '순위 미정'
									})()

									const displayPercentage =
										setting.percentage !== null
											? `${setting.percentage}%`
											: '미입력'
									return (
										<React.Fragment key={mapIndex}>
											<div className={styles.incentiveDisplayItem}>
												<span className={styles.incentiveRank}>
													{displayRange}
												</span>
												<span className={styles.incentiveSeparator}>:</span>
												<span className={styles.incentiveValue}>
													{displayPercentage}
												</span>
											</div>
										</React.Fragment>
									)
								})}
							</div>
						) : (
							<div className={styles.incentiveDisplayItem}>
								<div>인센티브 정보 없음</div>
							</div>
						)}
					</div>
				</div>
			)
		},
		[latestRenegotiation?.changedFields, sortIncentiveSettings],
	)

	const renderContractFields = useCallback(
		(contractType, contractData) => {
			const fields = CONTRACT_FIELD_CONFIGS[contractType] || []

			return fields.map((field) => {
				if (field.type === 'incentive') {
					return renderIncentiveField(field, contractData?.[field.key], contractType)
				}
				return renderContractField(field, contractData, contractType)
			})
		},
		[renderContractField, renderIncentiveField],
	)

	const handleCaddieAccept = async () => {
		try {
			await acceptOfferMutation.mutateAsync({ token, offerId: suggestManagementDetail.id })
			console.log('제안 수락 성공')
			navigate('/offers')
		} catch (error) {
			console.error('제안 수락 실패:', error)
		}
	}

	const handleRefuse = async () => {
		console.log('handleRefuse')
		try {
			await rejectOfferMutation.mutateAsync({ token, offerId: suggestManagementDetail.id })
			setRefuseModalOpen(false)
		} catch (error) {
			console.error('제안 거절 실패:', error)
		}
	}

	const handleAccept = async () => {
		console.log('handleAccept')
		try {
			await handleCaddieAccept()
			setAcceptModalOpen(false)
		} catch (error) {
			console.error('제안 수락 실패:', error)
		}
	}

	// displayIcon 렌더링을 useMemo로 최적화 - 사용자 타입별로 다르게 표시
	const displayIconContent = useMemo(() => {
		if (!suggestManagementDetail || !whichToLook) return null

		if (whichToLook?.user_type === 'tourPro') {
			// 프로: 우승, TOP10, 컷 통과율 표시 (서버 데이터에 따라 null 체크)
			const proData = whichToLook.user_tour_pro
			return (
				<>
					{proData?.wins && proData.wins > 0 && (
						<div className={styles.displayIcon}>우승 {proData.wins}회</div>
					)}
					{proData?.top10_count && proData.top10_count > 0 && (
						<div className={styles.displayIcon}>TOP10 {proData.top10_count}회</div>
					)}
					{proData?.cut_through_rate && (
						<div className={styles.displayIcon}>
							컷 통과율 {proData.cut_through_rate}%
						</div>
					)}
				</>
			)
		} else if (whichToLook?.user_type === 'tourCaddie') {
			// 캐디: 언어능력, 주요 커리어 표시 (서버 데이터 구조에 맞게 수정)
			const caddieData = whichToLook.user_tour_caddie
			return (
				<>
					{caddieData?.caddie_languages && caddieData.caddie_languages.length > 0 && (
						<div className={styles.displayIcon}>
							{caddieData.caddie_languages
								.map((lang) => lang.language_name)
								.join(', ')}{' '}
							가능
						</div>
					)}
					{caddieData?.caddie_careers && caddieData.caddie_careers.length > 0 && (
						<div className={styles.displayIcon}>
							{(() => {
								// 대표 대회 찾기
								const representativeCareer = caddieData.caddie_careers.find(
									(career) => career.is_representative,
								)

								if (representativeCareer) {
									if (representativeCareer.tournament_rank) {
										const rankNumber = mapRankToNumber(
											representativeCareer.tournament_rank,
										)
										return `${representativeCareer.tournament_name || '대회'} ${rankNumber}위`
									} else if (representativeCareer.tournament_name) {
										return `${representativeCareer.tournament_name} 참가`
									}
								}

								// 대표 대회가 없으면 최고 성적 찾기
								const careersWithRank = caddieData.caddie_careers.filter(
									(career) => career.tournament_rank,
								)
								if (careersWithRank.length > 0) {
									const bestCareer = careersWithRank.reduce((best, current) => {
										const currentRank = mapRankToNumber(current.tournament_rank)
										const bestRank = mapRankToNumber(best.tournament_rank)
										return parseInt(currentRank) < parseInt(bestRank)
											? current
											: best
									})
									const rankNumber = mapRankToNumber(bestCareer.tournament_rank)
									return `${bestCareer.tournament_name || '대회'} ${rankNumber}위`
								}

								// 순위 정보가 없으면 최근 대회 표시
								const sortedCareers = caddieData.caddie_careers.sort((a, b) => {
									return (
										new Date(b.tournament_date || b.created_at) -
										new Date(a.tournament_date || a.created_at)
									)
								})
								const recentCareer = sortedCareers[0]

								if (recentCareer?.tournament_name) {
									return `${recentCareer.tournament_name} 참가`
								} else {
									return `${caddieData.caddie_careers.length}개 대회 참가`
								}
							})()}
						</div>
					)}
				</>
			)
		}

		return null
	}, [suggestManagementDetail, whichToLook])

	// 로딩 상태 처리
	if (isLoading) {
		return (
			<div className={styles.suggestManagementDetailViewContainer}>
				<div style={{ padding: '20px', textAlign: 'center' }}>로딩 중...</div>
			</div>
		)
	}

	// 에러 상태 처리
	if (error) {
		return (
			<div className={styles.suggestManagementDetailViewContainer}>
				<div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
					데이터를 불러오는 중 오류가 발생했습니다.
				</div>
			</div>
		)
	}

	return (
		<div className={styles.suggestManagementDetailViewContainer}>
			{isHistoryModalOpen && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						zIndex: 999,
					}}
					onClick={() => setIsHistoryModalOpen(false)}>
					<div
						ref={historyModalRef}
						style={{
							width: 'fit-content',
							height: 'fit-content',
							position: 'fixed',
							top: 0,
							left: 0,
							zIndex: 1000,
							cursor: isHistoryModalDragging ? 'grabbing' : 'grab',
						}}
						onMouseDown={handleHistoryModalMouseDown}
						onClick={(e) => e.stopPropagation()}>
						<HistoryModal
							onClose={() => setIsHistoryModalOpen(false)}
							offerHistory={sortedOfferHistory}
						/>
					</div>
				</div>
			)}
			<div className={styles.backButton} onClick={() => navigate('/offers')}>
				<ArrowLeftIcon className={styles.backButtonIcon} />
				뒤로
			</div>
			<div className={styles.suggestManagementDetailCardContainer}>
				<div className={styles.suggestManagementDetailCardHeader}>
					<div className={styles.suggestManagementDetailCardHeaderContent}>
						<div className={styles.creationDate}>
							{suggestManagementDetail?.created_at?.slice(0, 10)}
						</div>
						<div className={styles.headerStatus}>
							{sortedOfferHistory?.length > 0 && (
								<div
									className={styles.historyStatus}
									// onClick={() => setIsHistoryModalOpen(true)}
								>
									<ArrowsRightLeftIcon className={styles.historyStatusIcon} />
									{sortedOfferHistory?.length}
								</div>
							)}
							{suggestManagementDetail?.offer_status !== 'REJECTED' &&
								suggestManagementDetail?.offer_status !== 'REJECT' && (
									<div className={styles.itemStatus}>{currentItemStatus}</div>
								)}
							{(suggestManagementDetail?.offer_status === 'REJECTED' ||
								suggestManagementDetail?.offer_status === 'REJECT') && (
								<div className={styles.itemStatus + ' ' + styles.itemStatusRefuse}>
									{currentItemStatus}
								</div>
							)}
							{/* <div className={styles.favorite}>
								{isFavorite ? (
									<StarIconSolid
										className={
											styles.favoriteIcon + ' ' + styles.favoriteIconSolid
										}
										onClick={() => setIsFavorite(!isFavorite)}
									/>
								) : (
									<StarIconOutline
										className={styles.favoriteIcon}
										onClick={() => setIsFavorite(!isFavorite)}
									/>
								)}
							</div> */}
						</div>
					</div>
				</div>
				<div className={styles.suggestManagementDetailCardMainContainer}>
					<div className={styles.mainContent}>
						<div className={styles.profileImageArea}>
							<img
								src={whichToLook?.profile_image || '/images/pro-default.png'}
								alt="profileImage"
								className={styles.profileImage}
								onError={(e) => {
									e.target.src = '/images/pro-default.png'
								}}
							/>
						</div>
						<div className={styles.profileDetailArea}>
							<div className={styles.profileMainInfo}>{displayTitle}</div>
							{displaySubInfo && (
								<div className={styles.profileSubInfo}>{displaySubInfo}</div>
							)}
						</div>
					</div>
					<div className={styles.displayIconContainer}>{displayIconContent}</div>
				</div>
				{authStatus?.user?.id === suggestManagementDetail?.user_id ? (
					<div
						className={`${styles.indicateSended} ${
							suggestManagementDetail?.offer_status === 'REJECTED' ||
							suggestManagementDetail?.offer_status === 'REJECT' ||
							suggestManagementDetail?.offer_status === 'OUTDATED'
								? styles.none
								: ''
						}`}>
						제안 발송 완료
					</div>
				) : (
					!(
						suggestManagementDetail?.offer_status === 'REJECTED' ||
						suggestManagementDetail?.offer_status === 'REJECT' ||
						suggestManagementDetail?.offer_status === 'OUTDATED' ||
						suggestManagementDetail?.offer_status === 'CONTRACT_PROPOSED'
					) && (
						<div className={styles.tabContainer}>
							<div
								className={
									tabStatus === 'offerContract'
										? styles.tabItem + ' ' + styles.tabItemActive
										: styles.tabItem
								}
								onClick={() => {
									setTabStatus('offerContract')
									if (authStatus?.user?.userType === 'tourPro') {
										setOfferModalOpen(true)
									} else {
										setAcceptModalOpen(true)
									}
								}}>
								{authStatus?.user?.userType === 'tourPro'
									? '계약 제안하기'
									: '제안 수락하기'}
							</div>
							<div
								className={
									tabStatus === 'reNegotiation'
										? styles.tabItem + ' ' + styles.tabItemActive
										: styles.tabItem
								}
								onClick={() => {
									setTabStatus('reNegotiation')
									navigate(`/offers/${offerId}/renegotiation`)
								}}>
								조건 재협상하기
							</div>
							<div
								className={
									tabStatus === 'refuse'
										? styles.tabItem + ' ' + styles.tabItemActive
										: styles.tabItem
								}
								onClick={() => setRefuseModalOpen(true)}>
								거절하기
							</div>
						</div>
					)
				)}
				{isContractProposed && (
					<div className={styles.contractProposedNotice}>
						계약이 제안되었습니다. 상세 내용은{' '}
						<span
							className={styles.contractProposedLink}
							onClick={() => navigate('/contracts')}>
							[계약제안]
						</span>{' '}
						페이지에서 확인하세요.
					</div>
				)}
			</div>
			{refuseModalOpen && (
				<RefuseModal onClose={() => setRefuseModalOpen(false)} onRefuse={handleRefuse} />
			)}
			{acceptModalOpen && (
				<AcceptModal onClose={() => setAcceptModalOpen(false)} onAccept={handleAccept} />
			)}
			{contractConfirmModalOpen && (
				<SpoeumModal
					onClose={() => {
						setContractConfirmModalOpen(false)
						// navigate('/contracts')
					}}
					title="계약서 서명 요청이 발송되었어요!"
					detail={
						<>
							지금{' '}
							<span style={{ fontWeight: 'bold', color: '#085e4b' }}>
								카카오톡/이메일
							</span>{' '}
							을 확인하여 계약서에 서명을
							<br /> 완료해 주세요. 서명이 완료되는 즉시 캐디에게 계약 제안이
							전달됩니다.
						</>
					}
					confirmButtonText="확인"
					hideCancelButton={true}
				/>
			)}
			<div className={styles.senderHello}>
				{authStatus?.user?.id === suggestManagementDetail?.target_user_id
					? suggestManagementDetail?.message
					: authStatus?.user?.id === suggestManagementDetail?.user_id
						? suggestManagementDetail?.message
						: '-'}
			</div>
			<div className={styles.basicContractInfoHeader}>기본 계약정보</div>
			<div className={styles.basicContractInfoTourType}>
				<div className={styles.basicLabel}>투어종류</div>
				<div className={styles.basicContent}>{tourCategories || '미정'}</div>
			</div>
			<div className={styles.basicContractInfoContractType}>
				<div className={styles.basicLabel}>계약유형</div>
				<div className={styles.basicContent}>{contractTypeLabel}</div>
			</div>
			<div
				className={
					// suggestManagementDetail?.termContract !== null
					// 	? styles.generalContractPeriodContainer
					// 	: styles.nonGeneralContractPeriodContainer
					styles.periodContainer
				}>
				<div className={styles.periodLabel}>계약기간</div>
				<div className={styles.periodContent}>{periodContractText}</div>
			</div>
			<div className={styles.tourListContainer}>
				<div className={styles.tourListLabel}>대회목록</div>
				<div className={styles.tourListContent}>
					{(!suggestManagementDetail?.Offer_tour ||
						suggestManagementDetail?.Offer_tour.length === 0) &&
						'미정'}
					{suggestManagementDetail?.Offer_tour?.length > 0 &&
						sortTourByDate(suggestManagementDetail?.Offer_tour).map((tour, index) => (
							<div key={index} className={styles.tourListItemContainer}>
								<div className={styles.tourListItemIndex}>({index + 1})</div>
								<div className={styles.tourListItemMain}>
									<div className={styles.tourName}>{tour.tour.name}</div>
									<div className={styles.tourDate}>
										{getYYYYMMString(tour.tour.started_at).slice(0, 8)} ~{' '}
										{getYYYYMMString(tour.tour.ended_at).slice(0, 8)}
									</div>
								</div>
							</div>
						))}
				</div>
			</div>
			<div className={styles.isOverSeasTourIncludeContainer}>
				<div className={styles.isOverSeasTourIncludeLabel}>해외 대회 포함 여부</div>
				<div className={styles.isOverSeasTourIncludeContent}>
					{overseasTourIncluded ? '해외대회 포함' : '해외대회 미포함'}
				</div>
			</div>
			<div className={styles.detailContractHeader}>상세 계약정보</div>
			{sortedOfferHistory?.length > 0 &&
				(latestRenegotiation?.changedFields?.length ?? 0) === 0 && (
					<div className={styles.noChangesLabel}>
						마지막 제안에서 변경된 내용이 없어요
					</div>
				)}
			{suggestManagementDetail?.termContract !== null && (
				<>{renderContractFields('termContract', suggestManagementDetail?.termContract)}</>
			)}
			{suggestManagementDetail?.domesticContract !== null &&
				suggestManagementDetail?.overseasContract !== null && (
					<>
						<div className={styles.domesticOverseasSwapContainer}>
							<button
								className={
									domesticOverseasSwapContent
										? styles.seeDomesticChosen
										: styles.seeDomesticUnchosen
								}
								onClick={() => setDomesticOverseasSwapContent((prev) => !prev)}>
								국내
							</button>
							<button
								className={
									domesticOverseasSwapContent
										? styles.seeOverseasUnchosen
										: styles.seeOverseasChosen
								}
								onClick={() => setDomesticOverseasSwapContent((prev) => !prev)}>
								해외
							</button>
						</div>
						{domesticOverseasSwapContent && (
							<>
								{renderContractFields(
									'domesticContract',
									suggestManagementDetail?.domesticContract,
								)}
							</>
						)}
						{!domesticOverseasSwapContent && (
							<>
								{renderContractFields(
									'overseasContract',
									suggestManagementDetail?.overseasContract,
								)}
							</>
						)}
					</>
				)}

			{suggestManagementDetail?.overseasContract !== null &&
				suggestManagementDetail?.domesticContract === null && (
					<>
						{renderContractFields(
							'overseasContract',
							suggestManagementDetail?.overseasContract,
						)}
					</>
				)}
			{suggestManagementDetail?.domesticContract !== null &&
				suggestManagementDetail?.overseasContract === null && (
					<>
						{renderContractFields(
							'domesticContract',
							suggestManagementDetail?.domesticContract,
						)}
					</>
				)}
			{offerModalOpen && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						zIndex: 999,
					}}
					onClick={() => setOfferModalOpen(false)}>
					<div
						ref={offerModalRef}
						style={{
							width: 'fit-content',
							height: 'fit-content',
							position: 'fixed',
							top: 0,
							left: 0,
							zIndex: 1000,
							cursor: isDragging ? 'grabbing' : 'grab',
						}}
						onMouseDown={handleMouseDown}
						onClick={(e) => e.stopPropagation()}>
						<OfferModal
							isOpen={offerModalOpen}
							onClose={(success) => {
								setOfferModalOpen(false)
								if (success) {
									setContractConfirmModalOpen(true)
								}
							}}
							data={suggestManagementDetail}
						/>
					</div>
				</div>
			)}

			{/* <OfferModal
				isOpen={offerModalOpen}
				onClose={() => setOfferModalOpen(false)}
				data={suggestManagementDetail}
			/> */}
		</div>
	)
})
